import type { Post } from "./types";
import heroNya from "@/assets/news/nya-smslan-2026.webp";
import heroUtanInkomst from "@/assets/news/smslan-utan-inkomst.webp";

export const POSTS_8: Record<string, Post> = {
  "nya-smslan-2026": {
    title: "Nya smslån 2026 – nya långivare, nya regler och vad som faktiskt är bättre",
    intro:
      "Marknaden för nya smslån ser inte likadan ut som för bara ett par år sedan. Nya långivare har kommit in, gamla har försvunnit och lagstiftningen har skärpts rejält. Vi förstår att det är svårt att veta vem man kan lita på när det dyker upp nya aktörer hela tiden – särskilt när du behöver pengar snabbt och inte har tid att läsa det finstilta. Här går vi lugnt igenom vad som är nytt 2026, hur du känner igen en seriös aktör, vad ett smslån får kosta enligt lag och när du gör klokare i att välja något helt annat.",
    author: "Erik Lindqvist",
    authorTitle: "finansiell skribent, 9 år inom konsumentkrediter",
    published: "16 augusti 2026",
    updated: "16 augusti 2026",
    heroSrc: heroNya,
    heroAlt: "Person jämför nya smslån 2026 i mobilen med BankID och bankapp",
    topLenderId: "credifi",
    bottomLenderId: "brixokk",
    topLenderTitle: "Populärast bland nya smslån just nu",
    bottomLenderTitle: "Vår rekommendation för dig som vill ha tydliga villkor",
    cta: { to: "/smslan", label: "Jämför smslån och nya långivare" },
    faq: [
      {
        q: "Vad menas egentligen med nya smslån?",
        a: "Med nya smslån menas dels långivare som nyligen lanserat sig på den svenska marknaden, dels befintliga aktörer som lanserat nya kreditprodukter – ofta kontokrediter i stället för klassiska engångslån. Gemensamt är digital ansökan med BankID och snabb utbetalning.",
      },
      {
        q: "Är nya långivare säkrare eller sämre än de etablerade?",
        a: "Varken eller automatiskt. Det avgörande är om långivaren har tillstånd hos eller står under Finansinspektionens tillsyn, redovisar full kostnadsinformation och gör en riktig kreditprövning. En ny aktör som uppfyller det är minst lika trygg som en gammal.",
      },
      {
        q: "Vad är räntetaket för nya smslån 2026?",
        a: "Sedan de nya reglerna för konsumentkrediter trädde i kraft får den nominella räntan högst vara 20 procentenheter över Riksbankens referensränta. Den effektiva räntan kan bli högre eftersom avgifter räknas in.",
      },
      {
        q: "Vad innebär kostnadstaket?",
        a: "Kostnadstaket betyder att summan av ränta, avgifter och dröjsmålsränta aldrig får överstiga det belopp du lånade. Lånar du 5 000 kr kan du alltså aldrig bli skyldig mer än 10 000 kr totalt.",
      },
      {
        q: "Erbjuder nya smslån utbetalning direkt?",
        a: "De flesta gör det. Har du konto i en storbank som stödjer realtidsbetalningar kan pengarna finnas på kontot inom cirka 15 minuter efter signering, även på kvällar och helger.",
      },
      {
        q: "Finns det nya smslån utan UC?",
        a: "Ja, flera nya aktörer använder Creditsafe eller Dun & Bradstreet (tidigare Bisnode) i stället för UC. Kreditprövning görs alltid enligt lag – skillnaden är bara vilket register som används och att ansökan inte syns i UC.",
      },
      {
        q: "Hur vet jag att en ny långivare inte är en bluff?",
        a: "Kontrollera organisationsnummer, sök upp bolaget i Finansinspektionens företagsregister, leta efter fullständig prislista och räkneexempel och se upp med aktörer som kräver förskottsavgift eller lovar garanterat godkännande.",
      },
      {
        q: "Bör jag byta till en ny långivare för att få lägre ränta?",
        a: "Det kan löna sig, men räkna alltid på totalkostnaden i kronor inklusive uppläggnings- och aviavgift. En lägre nominell ränta kan bli dyrare än ett dyrare lån med färre avgifter.",
      },
    ],
    blocks: [
      { t: "h2", text: "Därför söker så många efter nya smslån just nu" },
      { t: "p", text: "Har du fått avslag hos en långivare du känner till sedan tidigare och undrar om det finns någon ny som säger ja? Eller känner du bara att villkoren hos din nuvarande kreditgivare har blivit sämre? Vi förstår båda känslorna. Att leta efter nya smslån handlar sällan om nyfikenhet – det handlar oftast om att man behöver ett alternativ, snabbt, och att de gamla dörrarna känns stängda." },
      { t: "p", text: "Det goda i det hela är att marknaden faktiskt rör på sig. Under de senaste åren har flera nya långivare och nya kreditprodukter kommit till Sverige, samtidigt som lagstiftningen skärpts till konsumentens fördel. Det innebär att en ny aktör 2026 i regel måste vara tydligare med sina kostnader än vad många gamla aktörer var för fem år sedan." },
      { t: "p", text: "Samtidigt ska vi vara ärliga: nytt är inte samma sak som billigt. Ett smslån är fortfarande en av de dyraste formerna av lån du kan ta, och det ska användas till kortsiktiga behov – aldrig som en långsiktig del av din ekonomi." },
      {
        t: "stats",
        items: [
          { label: "Högsta nominella ränta enligt räntetaket", value: "22,00 %" },
          { label: "Maxkostnad utöver lånebeloppet (kostnadstaket)", value: "100 %" },
          { label: "Vanlig utbetalningstid vid direktutbetalning", value: "~15 min" },
        ],
      },

      { t: "h2", text: "Vad räknas som ett nytt smslån 2026?" },
      { t: "p", text: "Begreppet nya smslån används lite slarvigt i sökresultaten. I praktiken menas tre olika saker, och skillnaden spelar roll för dig som ska välja." },
      { t: "h3", text: "1. Helt nya långivare på svenska marknaden" },
      { t: "p", text: "Det här är bolag som fått tillstånd eller registrerat sig hos Finansinspektionen och som börjat marknadsföra sig i Sverige. De är ofta digitala från grunden, jobbar med automatiserad kreditbedömning och kan ha generösare bedömning än en storbank – men sällan lägre pris." },
      { t: "h3", text: "2. Nya produkter från etablerade aktörer" },
      { t: "p", text: "Många kända långivare har bytt spår och lanserat kontokrediter i stället för klassiska engångslån. Det är fortfarande samma bolag, men produkten är ny – och prissättningen fungerar annorlunda, eftersom du bara betalar ränta på det utrymme du faktiskt använder." },
      { t: "h3", text: "3. Nya aktörer i låneförmedlingsledet" },
      { t: "p", text: "En låneförmedlare lånar inte ut egna pengar utan skickar din ansökan vidare till flera långivare samtidigt. Nya förmedlare dyker upp regelbundet, och för dig innebär det att en ansökan kan ge flera svar utan att du behöver ansöka på tio ställen." },
      {
        t: "table",
        caption: "Skillnaderna i korthet mellan de tre typerna av nya aktörer.",
        head: ["Typ av aktör", "Vem lånar ut", "Antal kreditupplysningar", "Passar dig som"],
        rows: [
          ["Ny direktlångivare", "Långivaren själv", "1 per ansökan", "vet exakt vilket lån du vill ha"],
          ["Ny kontokredit", "Kreditinstitutet", "1 vid ansökan om kreditutrymme", "har ett återkommande behov"],
          ["Ny låneförmedlare", "Flera anslutna banker", "Oftast 1 gemensam", "vill jämföra flera erbjudanden"],
        ],
      },

      { t: "h2", text: "De nya reglerna som gäller alla smslån 2026" },
      { t: "p", text: "Den viktigaste förändringen de senaste åren är inte vilka bolag som finns, utan vad de får ta betalt. De skärpta reglerna i konsumentkreditlagen omfattar numera alla konsumentkrediter – inte bara de som tidigare klassades som högkostnadskrediter." },
      { t: "h3", text: "Räntetaket" },
      { t: "p", text: "Den nominella räntan får inte överstiga Riksbankens referensränta plus 20 procentenheter. Med en referensränta på 2,00 % innebär det ett tak på 22,00 % nominell ränta. Observera att den effektiva räntan – där avgifter räknas in – kan bli betydligt högre, särskilt på små belopp och korta löptider." },
      { t: "h3", text: "Kostnadstaket" },
      { t: "p", text: "Summan av ränta, avgifter, förseningsavgifter och dröjsmålsränta får aldrig överstiga det ursprungliga lånebeloppet. Det här är ett riktigt starkt skydd: även om det går riktigt fel kan skulden inte växa hur mycket som helst hos långivaren." },
      { t: "h3", text: "Kravet på kreditprövning" },
      { t: "p", text: "Långivaren är skyldig att göra en kreditprövning innan du beviljas ett lån. Ett bolag som marknadsför sig med garanterat godkännande bryter i praktiken mot lagen – och Konsumentverket har utfärdat både varningar och miljonböter mot bolag som slarvat här." },
      {
        t: "box",
        tone: "warning",
        title: "Att låna kostar pengar",
        text: "Om du inte kan betala tillbaka skulden i tid riskerar du en betalningsanmärkning. Det kan leda till svårigheter att hyra bostad, teckna abonnemang och få nya lån. För kostnadsfritt stöd, vänd dig till budget- och skuldrådgivningen i din kommun – kontaktuppgifter finns på [Konsumentverket](https://www.konsumentverket.se/).",
      },

      { t: "h2", text: "Så känner du igen en seriös ny långivare" },
      { t: "p", text: "När en aktör är ny finns det inga år av omdömen att luta sig mot. Då får du i stället titta på det som går att kontrollera på tio minuter." },
      {
        t: "steps",
        title: "Checklista innan du ansöker hos en ny långivare",
        items: [
          "Sök upp organisationsnumret och kontrollera bolaget i [Finansinspektionens](https://www.fi.se/) företagsregister.",
          "Leta upp en fullständig prislista med uppläggningsavgift, aviavgift och eventuell serviceavgift.",
          "Kontrollera att det finns ett representativt räkneexempel med effektiv ränta och total återbetalning.",
          "Se att villkoren är identiska i ansökan, i det finstilta och i marknadsföringen.",
          "Kontrollera att ångerrätten på 14 dagar och rätten till förtidsinlösen nämns.",
          "Undvik alla aktörer som kräver betalning i förskott eller lovar lån utan kreditprövning.",
        ],
      },
      { t: "h3", text: "Varningssignaler som bör få dig att backa" },
      {
        t: "ul",
        items: [
          "Formuleringar som \"garanterat lån\" eller \"alla beviljas\" – detta är inte förenligt med kravet på kreditprövning.",
          "Ingen svensk kundtjänst, ingen postadress och inget organisationsnummer.",
          "Kostnadsinformation som bara finns i en bild eller är gömd flera klick bort.",
          "Krav på att du betalar en avgift innan lånet betalas ut.",
          "Kontakt som sker via sms eller sociala medier från okänt nummer.",
        ],
      },

      { t: "h2", text: "Nya smslån med utbetalning direkt – så fungerar det" },
      { t: "p", text: "Många söker nya smslån just för att de behöver pengarna nu, inte på måndag. Utbetalningstiden avgörs egentligen av tre saker: när du signerar, vilken bank du har och om långivaren använder realtidsbetalningar." },
      {
        t: "table",
        caption: "Ungefärlig utbetalningstid beroende på situation. Tiderna är riktvärden, inte garantier.",
        head: ["Situation", "Vanlig utbetalningstid", "Kommentar"],
        rows: [
          ["Vardag, kontortid, storbankskonto", "5–30 minuter", "Snabbast möjliga scenario"],
          ["Kväll eller helg, storbankskonto", "15–60 minuter", "Kräver att långivaren erbjuder dygnet-runt-utbetalning"],
          ["Konto i mindre bank eller nischbank", "Nästa bankdag", "Realtidsbetalning stöds inte alltid"],
          ["Ansökan kräver manuell handläggning", "Samma dag till 1–2 dagar", "Vanligt vid oklar inkomst eller kompletteringar"],
        ],
      },
      { t: "h3", text: "Utbetalning via Swish" },
      { t: "p", text: "Vissa aktörer marknadsför utbetalning via Swish. I praktiken går pengarna oftast ändå till ditt bankkonto – Swish används mer som ett snabbt sätt att identifiera kontot. Läs mer i vår guide om [smslån via Swish](/nyhet/akut-sms-lan-1000-kr-swish)." },

      { t: "h2", text: "Nya aktörer utan UC – vad det betyder i praktiken" },
      { t: "p", text: "Att en långivare inte använder UC innebär inte att ingen kreditupplysning görs. Det innebär att upplysningen tas hos ett annat bolag, oftast Creditsafe eller Dun & Bradstreet. Ansökan syns då inte i din UC-rapport, vilket kan vara värdefullt om du redan har många förfrågningar där." },
      { t: "h3", text: "När det faktiskt hjälper" },
      { t: "p", text: "Om du har sökt lån på flera ställen den senaste tiden kan många UC-förfrågningar i sig sänka din kreditvärdighet. Då kan en långivare utan UC ge dig en rimligare chans. Vi går igenom det djupare i guiden om [smslån utan UC](/nyhet/sms-lan-utan-uc)." },
      { t: "h3", text: "När det inte hjälper" },
      { t: "p", text: "Har du en pågående utmätning hos Kronofogden eller en mycket ansträngd ekonomi kommer även en långivare utan UC att säga nej – för det är precis vad kreditprövningen är till för. Då är kommunens budget- och skuldrådgivning ett bättre första steg än ytterligare en ansökan." },

      { t: "h2", text: "Vad ett nytt smslån kostar – ett konkret räkneexempel" },
      { t: "p", text: "Det är lätt att stirra sig blind på procenten. Titta i stället på kronorna. Här är ett exempel som följer räntetaket och en löptid på minst tre månader, vilket är det vanligaste hos seriösa aktörer 2026." },
      {
        t: "table",
        caption: "Representativt räkneexempel. Exakta villkor varierar mellan långivare.",
        head: ["Post", "Belopp"],
        rows: [
          ["Lånebelopp", "5 000 kr"],
          ["Löptid", "6 månader"],
          ["Nominell ränta", "22,00 %"],
          ["Uppläggningsavgift", "295 kr"],
          ["Aviavgift", "45 kr/månad"],
          ["Ungefärlig månadskostnad", "≈ 953 kr"],
          ["Total återbetalning", "≈ 5 718 kr"],
          ["Total kreditkostnad", "≈ 718 kr"],
        ],
      },
      { t: "p", text: "Lägg märke till att avgifterna står för en stor del av kostnaden. Det är också därför den effektiva räntan blir hög på små och korta lån – de fasta avgifterna slås ut på få kronor och få månader. Vill du räkna på dina egna siffror kan du använda vår [lånekalkylator](/guider/lanekalkyl)." },
      {
        t: "box",
        tone: "tip",
        title: "Tre sätt att sänka kostnaden direkt",
        items: [
          "Låna det du faktiskt behöver – inte det maxbelopp du blir erbjuden.",
          "Välj längre löptid bara om du behöver lägre månadskostnad; kortare löptid ger lägre totalkostnad.",
          "Betala tillbaka i förtid när du kan – du har alltid rätt att lösa lånet och slipper då framtida ränta.",
        ],
      },

      { t: "h2", text: "Nytt smslån eller kontokredit – vad passar dig?" },
      { t: "p", text: "En av de tydligaste trenderna bland nya aktörer är att engångslånet ersätts av kontokrediten. Skillnaden är större än den låter." },
      {
        t: "table",
        head: ["", "Klassiskt smslån", "Kontokredit"],
        rows: [
          ["Utbetalning", "Hela beloppet på en gång", "Du tar ut det du behöver"],
          ["Ränta", "På hela beloppet", "Bara på utnyttjat belopp"],
          ["Avgifter", "Nya avgifter vid varje nytt lån", "Ofta en uppläggning, sedan löpande avgift"],
          ["Passar", "Enstaka, engångsbehov", "Återkommande, oregelbundna behov"],
        ],
      },
      { t: "h3", text: "Vår hållning" },
      { t: "p", text: "Har du ett engångsbehov och en tydlig återbetalningsplan är ett vanligt smslån oftast enklare att överblicka. Är behovet återkommande blir en kontokredit i regel billigare, eftersom du slipper betala uppläggningsavgift varje gång." },

      { t: "h2", text: "För- och nackdelar med att välja en ny långivare" },
      {
        t: "proscons",
        title: "Vår sammanvägda bild av nya smslån 2026",
        pros: [
          "Ofta modernare ansökningsflöden och snabbare besked",
          "Nya aktörer måste följa de skärpta reglerna från start",
          "Kan bevilja dig som fått avslag hos etablerade banker",
          "Fler väljer kreditupplysning utan UC",
          "Ökad konkurrens pressar avgifter på vissa produkter",
        ],
        cons: [
          "Kortare historik och färre kundomdömen att luta sig mot",
          "Priset är sällan lägre än hos etablerade aktörer",
          "Villkoren kan ändras snabbt under de första åren",
          "Risk för mindre utbyggd kundtjänst vid problem",
          "Enklare beviljande kan locka till att låna för mycket",
        ],
      },

      { t: "h2", text: "Alternativ som ofta blir billigare än ett nytt smslån" },
      { t: "p", text: "Innan du ansöker – gå igenom den här listan. Ett smslån ska vara det du landar i när inget annat fungerar, inte det första du provar." },
      {
        t: "ul",
        items: [
          "Be om anstånd eller avbetalningsplan hos den du är skyldig pengar – det är gratis och ofta enklare än man tror.",
          "Kontrollera om du redan har en outnyttjad kontokredit till lägre ränta.",
          "Undersök ett vanligt privatlån via en [låneförmedlare](/privatlan) om beloppet är större än 20 000 kr.",
          "Har du flera dyra krediter kan det löna sig att [samla lån](/samla-lan) till en lägre ränta.",
          "Sök kostnadsfri budget- och skuldrådgivning i din kommun om lånen börjar rulla på varandra.",
        ],
      },

      { t: "h2", text: "Vanliga misstag när man jämför nya smslån" },
      { t: "h3", text: "Att jämföra nominell ränta i stället för effektiv" },
      { t: "p", text: "Nominell ränta säger inget om avgifterna. Effektiv ränta räknar in dem – men även den kan bli missvisande på mycket korta lån. Bäst jämförelse: total återbetalning i kronor." },
      { t: "h3", text: "Att ansöka på tio ställen samma dag" },
      { t: "p", text: "Varje kreditupplysning kan påverka din bedömning. Använd hellre en förmedlare som gör en gemensam ansökan mot flera långivare." },
      { t: "h3", text: "Att låna mer än man behöver" },
      { t: "p", text: "Blir du erbjuden 20 000 kr när du behöver 4 000 kr – ta 4 000 kr. Det erbjudna beloppet är långivarens riskbedömning, inte en rekommendation." },

      { t: "h2", text: "Så gör du om du redan hamnat efter med betalningarna" },
      { t: "p", text: "Om du redan har svårt att betala en befintlig kredit vill vi vara raka: ett nytt smslån löser inte det problemet, det flyttar bara fram det. Kontakta i stället långivaren direkt och be om en betalningsplan – de flesta är förvånansvärt tillmötesgående när du hör av dig innan förfallodagen." },
      { t: "p", text: "Har det redan gått till inkasso eller Kronofogden finns det ändå vägar framåt. Läs vår guide om [skulder och betalningsanmärkningar hos Kronofogden](/nyhet/tips-nar-man-har-fatt-skulder-och-betalningsanmarkningar-hos-kronofogden), och boka tid hos kommunens budget- och skuldrådgivning. Den hjälpen är kostnadsfri och du behöver inte skämmas för att använda den." },

      { t: "h2", text: "Sammanfattning: så väljer du bland nya smslån" },
      { t: "p", text: "Nya smslån 2026 är i grunden en bra utveckling för dig som konsument – fler alternativ, tydligare regler och snabbare processer. Men nytt betyder inte billigt, och snabbt betyder inte tryggt. Kontrollera tillsyn, läs prislistan, räkna i kronor och låna aldrig mer än du kan betala tillbaka inom den tid du bestämt." },
      {
        t: "box",
        tone: "info",
        title: "Kom ihåg tre saker",
        items: [
          "Räntetaket ligger på 22,00 % nominell ränta med dagens referensränta.",
          "Kostnadstaket gör att du aldrig kan bli skyldig mer än dubbla lånebeloppet.",
          "En seriös aktör visar alltid ett fullständigt räkneexempel innan du ansöker.",
        ],
      },
    ],
  },

  "smslan-utan-inkomst": {
    title: "Smslån utan inkomst – vad som faktiskt gäller 2026",
    intro:
      "Ingen fast inkomst just nu, men räkningarna kommer ändå? Vi vet att det är en av de mest stressande situationer man kan hamna i, och att många långivare svarar nej med hänvisning till just inkomsten. Här går vi ärligt igenom vad ett smslån utan inkomst faktiskt innebär 2026, vilka inkomster som faktiskt räknas (fler än du tror), varför kreditprövningen alltid görs och vilka vägar som ofta fungerar bättre än ett dyrt snabblån.",
    author: "Erik Lindqvist",
    authorTitle: "finansiell skribent, 9 år inom konsumentkrediter",
    published: "16 augusti 2026",
    updated: "16 augusti 2026",
    heroSrc: heroUtanInkomst,
    heroAlt: "Person utan fast inkomst går igenom räkningar och söker smslån utan inkomst",
    topLenderId: "klaralan",
    bottomLenderId: "credifi",
    topLenderTitle: "Flera långivare på en ansökan – störst chans till ja",
    bottomLenderTitle: "Vår rekommendation vid oregelbunden inkomst",
    cta: { to: "/lan-utan-uc", label: "Se långivare med mildare inkomstkrav" },
    faq: [
      {
        q: "Går det att få smslån helt utan inkomst?",
        a: "Nej, inte hos en seriös långivare. Konsumentkreditlagen kräver en kreditprövning, och den bygger på att du har en återbetalningsförmåga. Däremot räknas betydligt fler inkomstslag än bara lön.",
      },
      {
        q: "Vilka inkomster räknas vid en ansökan?",
        a: "Utöver lön räknas normalt a-kassa, sjukpenning, aktivitetsstöd, pension, föräldrapenning, studiebidragets bidragsdel i vissa fall, inkomst av näringsverksamhet och regelbundna kapitalinkomster.",
      },
      {
        q: "Kan jag få lån som arbetslös?",
        a: "Ja, om du har ersättning från a-kassan eller aktivitetsstöd och i övrigt har ordning på ekonomin. Ersättningen räknas som inkomst, men beloppet blir lägre och därmed också det du kan beviljas.",
      },
      {
        q: "Kan en medsökande hjälpa?",
        a: "Ofta ja. En medsökande med stabil inkomst kan höja chansen att bli beviljad och ge lägre ränta. Kom ihåg att medsökanden blir lika betalningsskyldig som du.",
      },
      {
        q: "Vad gör jag om alla säger nej?",
        a: "Se avslaget som information, inte som ett misslyckande. Kontakta kommunens kostnadsfria budget- och skuldrådgivning, ansök om ekonomiskt bistånd hos socialtjänsten om läget är akut och be om anstånd hos den du är skyldig pengar.",
      },
      {
        q: "Är CSN-pengar godkänt som inkomst?",
        a: "Studiemedel behandlas olika av olika långivare. Bidragsdelen kan i vissa fall räknas, medan lånedelen sällan gör det eftersom den redan är en skuld.",
      },
      {
        q: "Finns det långivare som beviljar alla?",
        a: "Nej. Marknadsföring som lovar garanterat godkännande är ett tydligt varningstecken och strider mot kravet på kreditprövning. Undvik sådana aktörer helt.",
      },
      {
        q: "Vad kostar ett smslån om jag har låg inkomst?",
        a: "Kostnaden styrs av ränta och avgifter, inte av din inkomst – men låg inkomst ger oftast lägre beviljat belopp och kortare löptid. Räntetaket ligger på 22,00 % nominell ränta med dagens referensränta.",
      },
    ],
    blocks: [
      { t: "h2", text: "Vi förstår situationen du är i" },
      { t: "p", text: "Att söka efter smslån utan inkomst är sällan något man gör för att man vill. Det är något man gör när hyran ska betalas, när kylen är tom eller när ett avslag redan har landat i inkorgen. Vi tänker inte döma – och vi tänker inte heller lova saker som inte stämmer." },
      { t: "p", text: "Det ärliga svaret är att ingen seriös svensk långivare beviljar lån till någon helt utan inkomst. Men det ärliga svaret innehåller också något hoppfullare: definitionen av inkomst är bredare än de flesta tror, och det finns flera vägar som fungerar bättre än ännu en avslagen ansökan." },
      {
        t: "stats",
        items: [
          { label: "Lagkrav på kreditprövning", value: "Alltid" },
          { label: "Räntetak nominell ränta 2026", value: "22,00 %" },
          { label: "Kostnad för kommunens skuldrådgivning", value: "0 kr" },
        ],
      },

      { t: "h2", text: "Varför kräver alla långivare en inkomst?" },
      { t: "p", text: "Kravet kommer inte i första hand från långivaren utan från lagen. Konsumentkreditlagen ålägger kreditgivare att göra en kreditprövning och bara bevilja krediten om du bedöms kunna betala tillbaka. Syftet är att skydda dig från att hamna i en skuldfälla." },
      { t: "h3", text: "Vad kreditprövningen faktiskt tittar på" },
      {
        t: "ul",
        items: [
          "Din regelbundna inkomst före och efter skatt.",
          "Dina befintliga lån, krediter och kreditutrymmen.",
          "Boendekostnad och antal personer i hushållet.",
          "Eventuella betalningsanmärkningar och skuldsaldo hos Kronofogden.",
          "Ett kvar-att-leva-på-belopp (KALP) som ska räcka efter alla utgifter.",
        ],
      },
      { t: "p", text: "Det innebär att det inte bara handlar om hur mycket du tjänar, utan hur mycket som blir kvar. Två personer med samma inkomst kan därför få helt olika svar." },
      {
        t: "box",
        tone: "warning",
        title: "Vad ett löfte om garanterat lån betyder",
        text: "En aktör som lovar att alla beviljas gör antingen ingen kreditprövning – vilket strider mot lagen – eller så är det ren bluffmarknadsföring. I båda fallen ska du hålla dig borta. Konsumentverket har utfärdat både varningar och miljonbelopp i sanktionsavgifter mot bolag med bristande kreditprövning.",
      },

      { t: "h2", text: "Inkomstslag som faktiskt räknas – fler än du tror" },
      { t: "p", text: "Många ger upp för tidigt eftersom de tolkar ordet inkomst som lön från en fast anställning. Så snävt är det sällan. Här är vad som normalt kan räknas." },
      {
        t: "table",
        caption: "Bedömningen varierar mellan långivare – tabellen visar den vanligaste hanteringen.",
        head: ["Inkomstslag", "Räknas normalt?", "Kommentar"],
        rows: [
          ["Fast lön", "Ja", "Starkast underlag"],
          ["Timanställning / vikariat", "Ofta", "Kräver ofta 3–6 månaders historik"],
          ["A-kassa", "Ja", "Räknas som ersättning, lägre belopp beviljas"],
          ["Aktivitetsstöd", "Ofta", "Behandlas som a-kassa hos många"],
          ["Sjukpenning / sjukersättning", "Ja", "Regelbunden utbetalning väger tungt"],
          ["Pension", "Ja", "Ofta mycket stabilt underlag"],
          ["Föräldrapenning", "Ofta", "Tidsbegränsning kan påverka löptiden"],
          ["Inkomst av näringsverksamhet", "Ja", "Kräver deklarationsunderlag"],
          ["Studiemedel (CSN)", "Ibland", "Bidragsdelen kan räknas, lånedelen sällan"],
          ["Försörjningsstöd", "Sällan", "Räknas normalt inte som kreditgrundande"],
        ],
      },
      { t: "h3", text: "Så maximerar du underlaget du redovisar" },
      { t: "p", text: "Redovisa alla dina regelbundna inkomster, inte bara den största. Har du både aktivitetsstöd och en extrainkomst från timarbete ska båda med. Många fyller bara i en siffra och får ett onödigt lågt beviljat belopp – eller ett avslag." },

      { t: "h2", text: "Lån som arbetslös – vad som gäller" },
      { t: "p", text: "Att vara arbetslös betyder inte automatiskt att du saknar inkomst. Har du ersättning från a-kassan eller aktivitetsstöd har du en regelbunden utbetalning, och den räknas hos de flesta långivare." },
      { t: "h3", text: "Det som ändå försvårar" },
      {
        t: "ul",
        items: [
          "Ersättningen är lägre än lön, vilket sänker beviljat belopp.",
          "Vissa långivare kräver en minsta årsinkomst, ofta 100 000–150 000 kr.",
          "Ersättningsperioden är tidsbegränsad, vilket kan korta löptiden du erbjuds.",
          "Har du dessutom betalningsanmärkning blir bedömningen betydligt hårdare.",
        ],
      },
      { t: "h3", text: "Det som förbättrar dina chanser" },
      {
        t: "ul",
        items: [
          "Ansök om ett litet belopp – ju mindre, desto lättare att bevilja.",
          "Använd en [låneförmedlare](/privatlan) så att en ansökan prövas hos flera långivare.",
          "Lägg till en medsökande med stabil inkomst om det är möjligt.",
          "Betala av eller avsluta oanvända kreditutrymmen innan du ansöker – de räknas som skuld.",
        ],
      },

      { t: "h2", text: "Lån med skuldsanering eller skuld hos Kronofogden" },
      { t: "p", text: "Om du befinner dig i pågående skuldsanering är svaret tyvärr tydligt: du får inte ta nya lån under saneringen, och det är en del av överenskommelsen med Kronofogden. Att ändå göra det riskerar att spräcka hela skuldsaneringen." },
      { t: "p", text: "Har du ett skuldsaldo hos Kronofogden utan pågående skuldsanering finns det ett fåtal långivare som gör en helhetsbedömning – men de är få och de är dyra. Vi går igenom situationen i detalj i vår guide om [lån trots betalningsanmärkning](/nyhet/betalningsanmarkning-lan)." },
      {
        t: "box",
        tone: "tip",
        title: "Ett bättre första samtal",
        text: "Kommunens budget- och skuldrådgivning är kostnadsfri, oberoende och har tystnadsplikt. De kan hjälpa dig att förhandla med borgenärer, ansöka om skuldsanering och lägga en budget som håller. Kontaktuppgifter finns hos [Konsumentverket](https://www.konsumentverket.se/) via Hallå konsument.",
      },

      { t: "h2", text: "Vad ett smslån kostar när inkomsten är låg" },
      { t: "p", text: "Priset på lånet påverkas inte av din inkomst – men konsekvensen av att inte klara betalningarna gör det. Med en låg och osäker inkomst är marginalen mindre, och då blir varje avgift kännbar." },
      {
        t: "table",
        caption: "Representativt räkneexempel för ett litet lån med löptid på minst tre månader.",
        head: ["Post", "Belopp"],
        rows: [
          ["Lånebelopp", "3 000 kr"],
          ["Löptid", "6 månader"],
          ["Nominell ränta", "22,00 %"],
          ["Uppläggningsavgift", "295 kr"],
          ["Aviavgift", "45 kr/månad"],
          ["Ungefärlig månadskostnad", "≈ 627 kr"],
          ["Total återbetalning", "≈ 3 761 kr"],
          ["Total kreditkostnad", "≈ 761 kr"],
        ],
      },
      { t: "p", text: "Fråga dig själv rakt ut: klarar jag 627 kr extra varje månad i ett halvår? Om svaret är osäkert är lånet fel lösning, oavsett hur akut situationen känns just nu. Räkna gärna på dina egna siffror i vår [lånekalkylator](/guider/lanekalkyl)." },

      { t: "h2", text: "Alternativ när inkomsten inte räcker till" },
      { t: "p", text: "Det här är den viktigaste delen av artikeln. Om du står utan inkomst finns det stöd som är gjorda för exakt din situation – och de kostar inget att använda." },
      {
        t: "steps",
        title: "Gör så här, i den här ordningen",
        items: [
          "Kontakta den du är skyldig pengar och be om anstånd eller delbetalning – gör det innan förfallodagen.",
          "Ansök om ekonomiskt bistånd hos socialtjänsten i din kommun om läget är akut.",
          "Boka tid hos kommunens budget- och skuldrådgivning för en helhetsgenomgång.",
          "Kontrollera hos Försäkringskassan och a-kassan om du har ersättning du inte tagit ut.",
          "Se över abonnemang, försäkringar och autogiron – många hittar flera hundra kronor i månaden här.",
          "Först därefter: överväg ett mindre lån, och bara med en tydlig återbetalningsplan.",
        ],
      },
      {
        t: "proscons",
        title: "Att låna med osäker inkomst – vår sammanvägda bild",
        pros: [
          "Kan lösa ett akut, tidsbegränsat problem",
          "Små belopp kan beviljas även med ersättning i stället för lön",
          "Medsökande kan förbättra både chans och ränta",
          "Kostnadstaket begränsar hur stor skulden kan bli",
        ],
        cons: [
          "Hög kostnad i förhållande till beloppet",
          "Stor risk att ett litet lån blir flera",
          "Betalningsanmärkning får långvariga konsekvenser",
          "Löser sällan grundproblemet med för låg inkomst",
        ],
      },

      { t: "h2", text: "Så undviker du att ett lån blir flera" },
      { t: "h3", text: "Sätt ett tak innan du ansöker" },
      { t: "p", text: "Bestäm exakt belopp och exakt sista återbetalningsdatum innan du öppnar ansökan. Skriv ner det. Det låter enkelt, men det är den vanligaste skillnaden mellan ett lån som fungerar och ett lån som växer." },
      { t: "h3", text: "Ta aldrig ett nytt lån för att betala ett gammalt" },
      { t: "p", text: "Det är den punkt där ett hanterbart problem blir en skuldspiral. Har du redan flera krediter är [samla lån](/samla-lan) eller skuldrådgivning rätt väg – inte ett nytt smslån." },
      { t: "h3", text: "Använd ångerrätten om du ändrar dig" },
      { t: "p", text: "Du har 14 dagars ångerrätt från avtalsdagen. Ångrar du dig betalar du tillbaka beloppet plus ränta för de dagar du haft pengarna. Det är ett verkligt skyddsnät – använd det om magkänslan säger fel." },

      { t: "h2", text: "Vanliga frågor vi får om inkomstkravet" },
      { t: "h3", text: "Räcker det att jag har jobb, även om jag är ny?" },
      { t: "p", text: "Provanställning godtas av vissa långivare men inte alla. Har du tidigare anställningshistorik och samma bransch ökar chansen." },
      { t: "h3", text: "Spelar det roll var jag bor?" },
      { t: "p", text: "Inte direkt, men boendekostnaden gör det. Bor du billigt blir ditt kvar-att-leva-på högre, och då bedöms du kunna klara en högre månadskostnad." },
      { t: "h3", text: "Hjälper det att ansöka flera gånger?" },
      { t: "p", text: "Nej, tvärtom. Många förfrågningar på kort tid sänker din bedömning. Vänta hellre, förbättra underlaget och ansök en gång via en förmedlare." },

      { t: "h2", text: "Sammanfattning" },
      { t: "p", text: "Smslån utan inkomst finns inte hos seriösa långivare – men lån med en annan inkomst än lön gör det ofta. Ta reda på vad som faktiskt räknas i din situation, redovisa allt, håll beloppet litet och sätt en plan innan du signerar." },
      { t: "p", text: "Och om svaret ändå blir nej: det är inte ett omdöme om dig som person. Det är ett system som säger att en ny skuld skulle göra din situation svårare. Då är kommunens kostnadsfria rådgivning nästa steg – och den hjälper fler än du tror." },
      {
        t: "box",
        tone: "warning",
        title: "Att låna kostar pengar",
        text: "Om du inte kan betala tillbaka skulden i tid riskerar du en betalningsanmärkning. Det kan leda till svårigheter att hyra bostad, teckna abonnemang och få nya lån. För stöd, vänd dig till budget- och skuldrådgivningen i din kommun. Kontaktuppgifter finns på [konsumentverket.se](https://www.konsumentverket.se/).",
      },
    ],
  },
};
